import { Info, X } from 'lucide-react';
import { currencySymbol } from '@/utils/formatCurrency';
import { SuggestedBidDisplay } from '@/components/campaign-create/create-adgroup/SuggestedBidDisplay';
import {
  SB_THEME_TARGETS,
  type SbAddedThemeTarget,
  type SbThemeBidRecommendation,
} from '@/utils/sbAdGroupCreate';

export function SbThemeTargetingGroups({
  themes,
  onChange,
  defaultBid,
  disabled,
  layout = 'picker',
  recommendations = [],
}: {
  themes: SbAddedThemeTarget[];
  onChange: (rows: SbAddedThemeTarget[]) => void;
  defaultBid: string;
  disabled?: boolean;
  layout?: 'picker' | 'added';
  recommendations?: SbThemeBidRecommendation[];
}) {
  const addedTypes = new Set(themes.map((row) => row.themeType));
  const symbol = currencySymbol() || '₹';
  const recByType = new Map(recommendations.map((row) => [row.themeType, row]));

  const recFor = (themeType: SbAddedThemeTarget['themeType'], added?: SbAddedThemeTarget) => {
    const rec = recByType.get(themeType);
    return {
      suggestedBid: added?.suggestedBid ?? rec?.suggestedBid ?? null,
      suggestedBidMin: added?.suggestedBidMin ?? rec?.suggestedBidMin ?? null,
      suggestedBidMax: added?.suggestedBidMax ?? rec?.suggestedBidMax ?? null,
    };
  };

  const addTheme = (themeType: SbAddedThemeTarget['themeType']) => {
    const def = SB_THEME_TARGETS.find((row) => row.themeType === themeType);
    if (!def || addedTypes.has(themeType)) return;
    const rec = recFor(themeType);
    onChange([
      ...themes,
      {
        ...def,
        ...rec,
        bid: rec.suggestedBid != null ? rec.suggestedBid.toFixed(2) : defaultBid,
      },
    ]);
  };

  if (layout === 'added') {
    if (themes.length === 0) return null;
    return (
      <ul>
        {themes.map((row) => (
          <li
            key={row.themeType}
            className="grid grid-cols-[1fr_5.5rem_4.5rem_7rem] items-start gap-2 border-b border-border py-2"
          >
            <div>
              <p className="text-sm leading-5">{row.label}</p>
              <p className="text-xs text-foreground-muted">Targeting group</p>
            </div>
            <p className="text-sm text-foreground-muted">Theme</p>
            <SuggestedBidDisplay
              suggested={recFor(row.themeType, row).suggestedBid}
              min={recFor(row.themeType, row).suggestedBidMin}
              max={recFor(row.themeType, row).suggestedBidMax}
            />
            <label className="flex h-8 items-center gap-1 rounded-md border border-border bg-surface px-1.5">
              <span className="text-xs text-foreground-muted">{symbol}</span>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={row.bid}
                onChange={(event) =>
                  onChange(
                    themes.map((item) =>
                      item.themeType === row.themeType ? { ...item, bid: event.target.value } : item
                    )
                  )
                }
                className="w-full bg-transparent text-sm outline-none"
                disabled={disabled}
                aria-label={`Bid for ${row.label}`}
              />
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${row.label}`}
                onClick={() => onChange(themes.filter((item) => item.themeType !== row.themeType))}
              >
                <X className="h-3.5 w-3.5 text-foreground-muted" />
              </button>
            </label>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_5rem_5.5rem_4rem] items-end gap-2 text-xs font-medium text-foreground-muted">
        <span className="inline-flex items-center gap-1">
          Targeting groups
          <Info className="h-3 w-3" aria-hidden />
        </span>
        <span>Match type</span>
        <span>Sugg. bid</span>
        <button
          type="button"
          className="justify-self-end text-sm font-normal text-brand disabled:opacity-40"
          disabled={disabled || SB_THEME_TARGETS.every((row) => addedTypes.has(row.themeType))}
          onClick={() => SB_THEME_TARGETS.forEach((row) => addTheme(row.themeType))}
        >
          Add all
        </button>
      </div>
      <ul className="divide-y divide-border border-y border-border">
        {SB_THEME_TARGETS.map((row) => {
          const added = themes.find((item) => item.themeType === row.themeType);
          return (
            <li key={row.themeType} className="grid grid-cols-[1fr_5rem_5.5rem_4rem] items-center gap-2 py-2 text-sm">
              <span className="inline-flex items-center gap-1">
                {row.label}
                <Info className="h-3 w-3 text-foreground-subtle" aria-hidden />
              </span>
              <span className="text-foreground-muted">Theme</span>
              <SuggestedBidDisplay
                suggested={recFor(row.themeType, added).suggestedBid}
                min={recFor(row.themeType, added).suggestedBidMin}
                max={recFor(row.themeType, added).suggestedBidMax}
              />
              {added ? (
                <span className="justify-self-end text-foreground-muted">Added</span>
              ) : (
                <button
                  type="button"
                  className="justify-self-end text-brand"
                  disabled={disabled}
                  onClick={() => addTheme(row.themeType)}
                >
                  Add
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
